import type { GaragesRequest } from "@App/apis/graphql/perimeterSearch/requests/garagesRequest";
import type { GaragePaginator } from "@App/apis/graphql/perimeterSearch/responses/garagePaginator";
import { PerimeterSearchContext } from "@App/contexts/apis/PerimeterSearchContext";
import { useGaragesRequestFromStore } from "@App/hooks/api/graphql/perimeterSearch/requests/factories/useGaragesRequestFromStore";
import { useGarageBadges } from "@App/hooks/garageList/useGarageBadges";
import { useRecommendedGarages } from "@App/hooks/garageList/useRecommendedGarages";
import type { State } from "@App/state";
import { getConfig } from "@App/utils/getConfig";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { setNetwork } from "@App/state/action-creators";

/**
 * Refactored useGarages hook
 * - Reduces re-renders using a single selector + shallowEqual
 * - Memoizes derived values (limit, request)
 * - Adds in-flight request guarding to avoid race conditions
 * - Uses functional state updates and useCallback for stable function identities
 * - Normalizes loading flags and error handling
 */
export const useGarages = () => {
  const perimeterSearchApi = useContext(PerimeterSearchContext);

  const dispatch = useDispatch();

  // Select all needed slices in one go to minimize renders
  const { organization, service, brand, place, premiumServices, search, dates, distance, sortBy } =
    useSelector(
      (s: State) => ({
        organization: s.orders.organization,
        service: s.orders.service,
        brand: s.orders.brand,
        place: s.orders.place,
        premiumServices: s.orders.premiumServices,
        search: s.filters.search,
        dates: s.filters.dates,
        distance: s.filters.distance,
        sortBy: s.filters.sortBy,
      }),
      shallowEqual,
    );

  // Config (memoized by organization)
  const limit = useMemo(
    () => getConfig(organization).garageList?.itemsPerPage ?? 100,
    [organization],
  );

  // Pagination
  const [offset, setOffset] = useState<number>(0);

  // Data + UI state
  const [garagePaginator, setGaragePaginator] = useState<GaragePaginator | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchMoreLoading, setFetchMoreLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const { recommendedGarages } = useRecommendedGarages();
  const { nearestGarage, bestReviewedGarage, earliestAvailableGarage, authorizedGarage } =
    useGarageBadges({ garagePaginator });

  // Build request from store (limit is memoized so this only changes when inputs change)
  const request = useGaragesRequestFromStore({ limit });

  // Reset pagination when core filters change (implicitly covered by request changes, but explicit for clarity)
  useEffect(() => {
    setOffset(0);
  }, [
    organization,
    service,
    brand,
    place,
    premiumServices,
    search,
    dates,
    distance,
    sortBy,
    limit,
  ]);

  // Guard against race conditions (keep only the latest response)
  const requestIdRef = useRef(0);

  const setLatestDataSafe = useCallback((id: number, updater: () => void) => {
    if (id === requestIdRef.current) updater();
  }, []);

  const getGarages = useCallback(async () => {
    if (!request || !perimeterSearchApi) return;

    const myId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const garages = await perimeterSearchApi.getGarages(request);
      setLatestDataSafe(myId, () => setGaragePaginator(garages));
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setLatestDataSafe(myId, () => setError(err));
    } finally {
      setLatestDataSafe(myId, () => setLoading(false));
    }
  }, [perimeterSearchApi, request, setLatestDataSafe]);

  const fetchMore = useCallback(
    async ({ variables }: { variables: Partial<GaragesRequest> }) => {
      if (!request || !perimeterSearchApi) return;

      const myId = ++requestIdRef.current;
      try {
        const garages = await perimeterSearchApi.getGarages({
          ...request,
          ...variables,
        });
        const newData = garages.data;

        setLatestDataSafe(myId, () => {
          setGaragePaginator((prev) => {
            if (!prev) return garages;
            return {
              ...garages,
              data: [...prev.data, ...newData],
            };
          });
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setLatestDataSafe(myId, () => setError(err));
      }
    },
    [perimeterSearchApi, request, setLatestDataSafe],
  );

  const loadMore = useCallback(async () => {
    if (fetchMoreLoading) return; // prevent double-tap

    setFetchMoreLoading(true);
    const nextOffset = offset + limit;
    try {
      await fetchMore({ variables: { offset: nextOffset } });
      setOffset(nextOffset);
    } finally {
      setFetchMoreLoading(false);
    }
  }, [fetchMore, fetchMoreLoading, offset, limit]);

  const refetch = useCallback(() => {
    if (!request || loading) return;
    void getGarages();
  }, [getGarages, loading, request]);

  // Initial + reactive load on request change
  useEffect(() => {
    if (!request) return;
    void getGarages();
  }, [request, getGarages]);

  const garages = garagePaginator?.data ?? null;
  const network = garagePaginator?.network ?? null;
  const paginatorInfo = garagePaginator?.paginatorInfo ?? null;
  const isFiltered = !!search;

  useEffect(() => {
    if (network === null) return;
    dispatch(setNetwork(network));
  }, [network]);

  return {
    loading,
    fetchMoreLoading,
    garages,
    paginatorInfo,
    isFiltered,
    recommendedGarages,
    error,
    nearestGarage,
    bestReviewedGarage,
    earliestAvailableGarage,
    authorizedGarage,
    refetch,
    loadMore,
  };
};
